import { useEffect, useMemo, useState } from "react";
import { getPredictiveLogisticsData } from "../../../api/predictiveLogisticsService";

function PredictiveLogisticsCenterPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = async () => {
        try {
            setLoading(true);
            setError("");

            const result = await getPredictiveLogisticsData();
            setData(result);
        } catch (e) {
            console.error(e);
            setError(
                e?.response?.data?.message ||
                e?.message ||
                "Failed to load predictive logistics data."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const kpis = useMemo(() => {
        if (!data) return [];

        return [
            ["Predicted Delays", data.predictedDelays ?? 0, "red"],
            ["Congested Routes", data.congestedRoutes ?? 0, "yellow"],
            ["Warehouse Load", `${data.warehouseLoad ?? 0}%`, "blue"],
            ["Fleet Utilization", `${data.fleetUtilization ?? 0}%`, "green"],
            ["ETA Accuracy", `${data.etaAccuracy ?? 0}%`, "teal"],
            ["Delivery Success", `${data.deliverySuccess ?? 0}%`, "purple"],
            ["AI Confidence", `${data.aiConfidence ?? 0}%`, "indigo"],
            ["Optimization Score", `${data.optimizationScore ?? 0}%`, "slate"],
        ];
    }, [data]);

    if (loading) {
        return <div className="p-6 text-slate-600">Loading Predictive Logistics...</div>;
    }

    if (error) {
        return (
            <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
                {error}
            </div>
        );
    }

    if (!data) {
        return (
            <div className="rounded-2xl bg-white border border-slate-200 shadow p-8 text-center">
                No prediction data available.
            </div>
        );
    }

    return (
        <div className="w-full">
            <div className="mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900">
                        Predictive Logistics Center
                    </h1>
                    <p className="text-slate-600 mt-2">
                        Artificial intelligence predicts future logistics performance,
                        delays, congestion and operational risks.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={load}
                    className="rounded-xl bg-slate-800 px-5 py-3 text-white font-semibold hover:bg-slate-900 transition"
                >
                    Refresh Predictions
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
                {kpis.map(([title, value, color]) => (
                    <KpiCard key={title} title={title} value={value} color={color} />
                ))}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
                <Panel title="Upcoming Delays" subtitle="Shipments with predicted delay risk.">
                    {data.upcomingDelays?.length > 0 ? (
                        <div className="space-y-3">
                            {data.upcomingDelays.map((x, i) => (
                                <DelayCard key={i} item={x} />
                            ))}
                        </div>
                    ) : (
                        <EmptyText text="No upcoming delays predicted." />
                    )}
                </Panel>

                <Panel title="Warehouse Forecast" subtitle="Predicted warehouse utilization and operational risk.">
                    {data.warehouses?.length > 0 ? (
                        <div className="space-y-3">
                            {data.warehouses.map((x, i) => (
                                <WarehouseCard key={i} item={x} />
                            ))}
                        </div>
                    ) : (
                        <EmptyText text="No warehouse forecast available." />
                    )}
                </Panel>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
                <Panel title="Fleet Forecast" subtitle="Vehicle engine and battery prediction.">
                    {data.fleet?.length > 0 ? (
                        <div className="grid md:grid-cols-2 gap-4">
                            {data.fleet.map((x, i) => (
                                <FleetCard key={i} item={x} />
                            ))}
                        </div>
                    ) : (
                        <EmptyText text="No fleet forecast available." />
                    )}
                </Panel>

                <Panel title="AI Recommendations" subtitle="Suggested optimization actions.">
                    {data.recommendations?.length > 0 ? (
                        <div className="space-y-3">
                            {data.recommendations.map((x, i) => (
                                <RecommendationCard key={i} text={x} />
                            ))}
                        </div>
                    ) : (
                        <EmptyText text="No AI recommendations available." />
                    )}
                </Panel>
            </div>

            <Panel title="Prediction Timeline" subtitle="Forecast horizon generated by the AI logistics model.">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-5">
                    <TimelineCard title="Today" value={data.timeline?.today} />
                    <TimelineCard title="Tomorrow" value={data.timeline?.tomorrow} />
                    <TimelineCard title="3 Days" value={data.timeline?.threeDays} />
                    <TimelineCard title="7 Days" value={data.timeline?.sevenDays} />
                    <TimelineCard title="30 Days" value={data.timeline?.thirtyDays} />
                </div>
            </Panel>
        </div>
    );
}

function KpiCard({ title, value, color = "blue" }) {
    const colors = {
        red: "bg-red-50 text-red-700",
        yellow: "bg-yellow-50 text-yellow-700",
        blue: "bg-blue-50 text-blue-700",
        green: "bg-green-50 text-green-700",
        teal: "bg-teal-50 text-teal-700",
        purple: "bg-purple-50 text-purple-700",
        indigo: "bg-indigo-50 text-indigo-700",
        slate: "bg-slate-50 text-slate-700",
    };

    return (
        <div className="bg-white rounded-2xl shadow border border-slate-200 p-6 hover:shadow-xl transition">
            <p className="text-slate-500">{title}</p>
            <div className={`inline-flex mt-3 px-3 py-1 rounded-xl font-black text-3xl ${colors[color]}`}>
                {value ?? "—"}
            </div>
        </div>
    );
}

function Panel({ title, subtitle, children }) {
    return (
        <div className="bg-white rounded-2xl shadow border border-slate-200 p-6">
            <h2 className="text-2xl font-bold">{title}</h2>
            {subtitle && <p className="text-slate-500 mt-1 mb-5">{subtitle}</p>}
            {children}
        </div>
    );
}

function DelayCard({ item }) {
    const probability = Number(item.probability ?? 0);

    return (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <h3 className="font-bold text-red-900">
                        {item.shipment || "Shipment"}
                    </h3>
                    <p className="text-red-700 mt-1">{item.delay || "Delay predicted"}</p>
                </div>

                <span className="rounded-xl bg-red-100 text-red-700 px-3 py-1 font-black">
                    {probability}%
                </span>
            </div>

            <ProgressBar score={probability} color="bg-red-500" />
        </div>
    );
}

function WarehouseCard({ item }) {
    const load = Number(item.load ?? 0);
    const risk = String(item.risk || "Unknown");

    return (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <h3 className="font-bold text-slate-900">
                        {item.name || "Warehouse"}
                    </h3>
                    <p className="text-slate-500 mt-1">Risk: {risk}</p>
                </div>

                <span className={`rounded-xl px-3 py-1 font-black ${getLoadBadge(load)}`}>
                    {load}%
                </span>
            </div>

            <ProgressBar
                score={load}
                color={load >= 85 ? "bg-red-500" : load >= 70 ? "bg-yellow-500" : "bg-green-500"}
            />
        </div>
    );
}

function FleetCard({ item }) {
    const engine = Number(item.engine ?? 0);
    const battery = Number(item.battery ?? 0);

    return (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <h3 className="font-bold text-slate-900 mb-4">
                🚛 {item.vehicle || "Vehicle"}
            </h3>

            <MetricBar label="Engine" value={engine} />
            <MetricBar label="Battery" value={battery} />
        </div>
    );
}

function RecommendationCard({ text }) {
    return (
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4">
            <h3 className="font-bold text-indigo-900">🤖 AI Recommendation</h3>
            <p className="text-indigo-700 mt-2">{text}</p>
        </div>
    );
}

function TimelineCard({ title, value }) {
    return (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center hover:bg-white hover:shadow transition">
            <p className="text-slate-500">{title}</p>
            <div className="mt-3 text-3xl font-black text-blue-700">
                {value ?? "—"}
            </div>
        </div>
    );
}

function MetricBar({ label, value }) {
    return (
        <div className="mb-4 last:mb-0">
            <div className="flex justify-between text-sm mb-2">
                <span className="font-semibold text-slate-700">{label}</span>
                <span className="font-bold">{value}%</span>
            </div>
            <ProgressBar
                score={value}
                color={value >= 80 ? "bg-green-500" : value >= 60 ? "bg-yellow-500" : "bg-red-500"}
            />
        </div>
    );
}

function ProgressBar({ score, color = "bg-blue-500" }) {
    return (
        <div className="mt-3 h-3 w-full rounded-full bg-white overflow-hidden">
            <div
                className={`h-full ${color}`}
                style={{ width: `${Math.min(Math.max(Number(score || 0), 0), 100)}%` }}
            />
        </div>
    );
}

function EmptyText({ text }) {
    return <p className="text-slate-500">{text}</p>;
}

function getLoadBadge(load) {
    if (load >= 85) return "bg-red-100 text-red-700";
    if (load >= 70) return "bg-yellow-100 text-yellow-700";
    return "bg-green-100 text-green-700";
}

export default PredictiveLogisticsCenterPage;